import { View } from 'react-native';
import { Text } from '@/components/ui/text';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Defs, LinearGradient, Stop, Svg, Rect } from 'react-native-svg';
import { useTranslation } from 'react-i18next';
import { LayoutBreakpoints } from '@/constants/LayoutBreakpoints';
import { useIsWidthUp } from '~/global/hooks/useIsWidthUp';

export interface ProfileAvatarSectionProps {
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string;
  createdAt?: Date | string | null;
}

export const ProfileAvatarSection = ({
  firstName,
  lastName,
  email,
  avatarUrl,
  createdAt,
}: ProfileAvatarSectionProps) => {
  const { t } = useTranslation('user');
  const isDesktop = useIsWidthUp(LayoutBreakpoints.desktop);

  const initials =
    `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`.toUpperCase();
  const fullName = `${firstName} ${lastName}`.trim();
  const parsedDate = createdAt ? new Date(createdAt) : null;
  const memberSince =
    parsedDate && !isNaN(parsedDate.getTime())
      ? t('settings.memberSince', {
          date: new Intl.DateTimeFormat('en', {
            month: 'long',
            year: 'numeric',
          }).format(parsedDate),
        })
      : null;

  return (
    <View className="items-center gap-4 xl:flex-row xl:items-start xl:gap-5">
      <View className="relative h-[100px] w-[100px]">
        <Avatar
          alt={fullName}
          className="h-[100px] w-[100px] rounded-full overflow-hidden"
        >
          <AvatarImage source={{ uri: avatarUrl }} />
          <AvatarFallback className="rounded-full">
            <Svg height="100%" width="100%" className="absolute inset-0">
              <Defs>
                <LinearGradient
                  id="profileAvatarGradient"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <Stop offset="0%" stopColor="#FFD700" />
                  <Stop offset="100%" stopColor="#FFA500" />
                </LinearGradient>
              </Defs>
              <Rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill="url(#profileAvatarGradient)"
              />
            </Svg>
            <Text className="font-sora text-2xl font-bold text-text-primary absolute">
              {initials}
            </Text>
          </AvatarFallback>
        </Avatar>
      </View>
      <View className="items-center gap-1 xl:items-start">
        <Text className="font-sora text-xl xl:text-2xl font-semibold">
          {fullName}
        </Text>
        <Text className="text-sm text-text-secondary">{email}</Text>
        {/* Member-since (desktop only). Conditionally rendered (not
            `display:none` via `hidden xl:flex`) to avoid the iOS New Arch
            phone-`display:none` re-render crash (RN #52349). */}
        {isDesktop && memberSince && (
          <Text className="text-xs text-text-muted">{memberSince}</Text>
        )}
      </View>
    </View>
  );
};
